import React, { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../actions/userActions";
import PromoBanner from "./promoBanner";
import SiteImage from "./SiteImage";

const Header = () => {
  const dispatch = useDispatch();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  let windowWidth = useRef(window.innerWidth);

  const logoutHandler = () => {
    dispatch(logout());
  };

  useEffect(() => {
    window.addEventListener("resize", recalculateWidth);
    return () => {
      window.removeEventListener("resize", recalculateWidth);
    };
  }, [windowWidth]);

  const recalculateWidth = () => {
    windowWidth.current = window.innerWidth;
  };

  // Each card's icon now comes from the admin-managed site-images flow
  // (keys: vision1 / vision2 / vision3). The local PNG keeps acting as a
  // fallback so nothing breaks until the admin actually uploads a custom
  // image from /admin/site-images.
  const features = [
    {
      imgKey: "vision1",
      img: require("../img/bottle1_1.png"),
      kicker: "01 — Ciencia",
      title: "Fórmulas avanzadas",
      body:
        "Nuestros productos cuentan con formulaciones avanzadas, meticulosamente elaboradas para abordar diversas preocupaciones del cuidado de la piel. Desde soluciones anti-envejecimiento hasta potenciadores de hidratación y fórmulas para combatir el acné, cada producto está diseñado para ofrecer resultados específicos.",
      tags: ["Anti-edad", "Hidratación", "Ciencia"],
    },
    {
      imgKey: "vision2",
      img: require("../img/natural1_1.png"),
      kicker: "02 — Naturaleza",
      title: "Ingredientes naturales",
      body:
        "Creemos en aprovechar el poder de la naturaleza para mejorar la salud y vitalidad de tu piel. Nuestros productos están enriquecidos con una mezcla seleccionada de extractos botánicos, aceites nutritivos, antioxidantes y vitaminas, diseñados para trabajar en armonía con tu piel.",
      tags: ["Botánicos", "Aceites", "Antioxidantes"],
    },
    {
      imgKey: "vision3",
      img: require("../img/woman1_1.png"),
      kicker: "03 — Eficacia",
      title: "Resultados visibles",
      body:
        "En el corazón de nuestra línea de productos está el compromiso de entregar resultados visibles. Nuestras formulaciones son desarrolladas pensando en la eficacia. Con el uso regular, te ayudarán a lograr mejoras notables en la textura, el tono y la apariencia de tu piel.",
      tags: ["Textura", "Tono", "Luminosidad"],
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Jost:wght@200;300;400;500&display=swap');

        .hadacy-vision {
          --cream: #f5f0e8;
          --dark: #1a1410;
          --gold: #b8933a;
          --gold-light: #d4af6a;
          --muted: #7a6e62;
          --card-bg: #faf7f2;

          background: var(--cream);
          color: var(--dark);
          font-family: "Jost", sans-serif;
          font-weight: 300;
          padding: 3rem 0 3rem;
          position: relative;

          /* Full-bleed: extend cream background to viewport edges,
             escaping any constrained parent (Bootstrap Container, etc.) */
          width: 100vw;
          left: 50%;
          right: 50%;
          margin-left: -50vw;
          margin-right: -50vw;
        }

        /* Subtle hairline separator between cream sections for editorial rhythm */
        .hadacy-vision::before {
          content: "";
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 70%;
          max-width: 900px;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(184,147,58,0.32), transparent);
        }
        .hadacy-vision * { box-sizing: border-box; }

        .hadacy-vision .hv-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        .hadacy-vision .hv-rule {
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(184,147,58,0.35), transparent);
          margin: 0 auto 2.4rem;
        }

        .hadacy-vision .hv-intro {
          text-align: center;
          max-width: 760px;
          margin: 0 auto 2.4rem;
        }
        .hadacy-vision .hv-kicker {
          font-size: 0.7rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.55rem;
        }
        .hadacy-vision .hv-intro h2 {
          font-family: "Cormorant Garamond", serif;
          font-size: clamp(1.8rem, 4vw, 2.6rem);
          font-weight: 400;
          color: var(--dark);
          margin: 0 0 0.6rem;
          line-height: 1.05;
        }
        .hadacy-vision .hv-intro p {
          font-size: 0.95rem;
          line-height: 1.75;
          color: var(--muted);
          max-width: 620px;
          margin: 0 auto;
        }
        .hadacy-vision .hv-intro-divider {
          width: 60px;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--gold), transparent);
          margin: 1rem auto 0;
        }

        .hadacy-vision .hv-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.25rem;
        }
        @media (max-width: 900px) {
          .hadacy-vision .hv-grid { grid-template-columns: 1fr; }
        }

        .hadacy-vision .hv-card {
          background: var(--card-bg);
          border: 1px solid rgba(184,147,58,0.18);
          border-radius: 18px;
          padding: 1.6rem 1.4rem 1.5rem;
          box-shadow: 0 10px 25px rgba(26,20,16,0.04);
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
        }
        .hadacy-vision .hv-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 18px 38px rgba(26,20,16,0.10);
          border-color: rgba(184,147,58,0.4);
        }

        .hadacy-vision .hv-card-img {
          width: 100%;
          aspect-ratio: 16 / 11;
          border-radius: 14px;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(184,147,58,0.10), rgba(232,213,196,0.30));
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 0.4rem;
        }
        /* SiteImage wraps its <img> in an extra div for the skeleton overlay.
           Make that wrapper a flex-center container so both the loading
           shimmer and the loaded image stay perfectly centred inside the
           16:11 frame (just like the previous bare <img> did). */
        .hadacy-vision .hv-card-img > div {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .hadacy-vision .hv-card-img img {
          width: 60%;
          height: 80%;
          object-fit: contain;
          display: block;
        }

        .hadacy-vision .hv-card-kicker {
          font-size: 0.62rem;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: var(--gold);
          font-weight: 400;
        }
        .hadacy-vision .hv-card h3 {
          font-family: "Cormorant Garamond", serif;
          font-weight: 400;
          font-size: 1.55rem;
          line-height: 1.15;
          color: var(--dark);
          margin: 0;
        }
        .hadacy-vision .hv-card-divider {
          height: 1px;
          background: linear-gradient(90deg, var(--gold-light), transparent);
          opacity: 0.4;
        }
        .hadacy-vision .hv-card-body {
          font-size: 0.86rem;
          line-height: 1.7;
          color: var(--muted);
          text-align: left;
          margin: 0;
        }
        .hadacy-vision .hv-card-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.35rem;
          /* Push tags to the bottom of the card so all three cards have
             their badges (Anti-edad, Botánicos, Textura, …) on the same
             baseline regardless of how long each body paragraph is.
             The grid already stretches every card to equal height; this
             flex auto-margin then pins the tag row to the bottom. */
          margin-top: auto;
          padding-top: 0.6rem;
        }
        .hadacy-vision .hv-tag {
          font-size: 0.6rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 0.2rem 0.55rem;
          border: 1px solid rgba(184,147,58,0.28);
          color: var(--gold);
          border-radius: 1px;
        }

        /* Promo banner spacing */
        .hadacy-vision .hv-promo {
          margin-top: 2.4rem;
        }
        .hadacy-vision .hv-promo .container { padding: 0; max-width: 1200px; }
      `}</style>

      <div className="hadacy-vision">
        <div className="hv-container">
          <div className="hv-rule" />

          <div className="hv-intro">
            <p className="hv-kicker">¿Por qué HADACY?</p>
            <h2>La diferencia está en cada gota</h2>
            <p>
              Tres pilares que guían cada uno de nuestros productos. Ciencia,
              naturaleza y resultados pensados para tu piel.
            </p>
            <div className="hv-intro-divider" />
          </div>

          <div className="hv-grid">
            {features.map((f, i) => (
              <article className="hv-card" key={i}>
                <div className="hv-card-img">
                  <SiteImage
                    keyName={f.imgKey}
                    fallback={f.img}
                    alt={f.title}
                    loaderHeight={180}
                  />
                </div>
                <span className="hv-card-kicker">{f.kicker}</span>
                <h3>{f.title}</h3>
                <div className="hv-card-divider" />
                <p className="hv-card-body">{f.body}</p>
                <div className="hv-card-tags">
                  {f.tags.map((t) => (
                    <span className="hv-tag" key={t}>{t}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <div className="hv-promo">
            <PromoBanner />
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
